/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  usePlayerAdvertisementTracker,
  usePlayerSettings,
} from "../../BasePlayer";
import { usePlayerPlayingState } from "../../Controller";
import { useOverlayState } from "../OverlayStateContext";

export const usePlayerOverlayVisibility = () => {
  const { isPlaying: isVideoPlaying } = usePlayerPlayingState();

  const {
    tracker: { playing: isAdPlaying },
  } = usePlayerAdvertisementTracker();

  const { settings } = useOverlayState();

  const {
    ui: { overlayVisibleMs },
  } = usePlayerSettings();

  const [isVisible, setVisible] = useState<boolean>(false);

  const mount = useRef<boolean>(false);
  const timer = useRef<number>();

  const isPlaying = isVideoPlaying || isAdPlaying;

  const updateStatus = useCallback(
    (value: boolean) => {
      // Always show overlay when player is stopped or any interactive/popup element is visible
      setVisible(!isPlaying || settings ? true : value);
    },
    [isPlaying, settings],
  );

  const setTimer = useCallback(() => {
    timer.current = window.setInterval(
      () => mount.current && updateStatus(false),
      overlayVisibleMs,
    );
  }, [updateStatus, overlayVisibleMs]);

  const onMouseMove = useCallback(() => {
    clearInterval(timer.current);

    // Mouse move always shows overlay
    mount.current && setVisible(true);

    setTimer();
  }, [setTimer]);

  useEffect(() => {
    mount.current = true;

    return () => {
      mount.current = false;
    };
  }, []);

  useEffect(() => {
    setTimer();

    return () => {
      clearInterval(timer.current);
    };
  }, [setTimer]);

  useEffect(() => {
    !isPlaying && setVisible(true);
  }, [isPlaying]);

  return {
    isVisible,
    onMouseMove,
  };
};
